import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const API = "https://dailydish.in/api";

const SESSIONS = ["Breakfast", "Lunch", "Dinner"];

const selectStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: "1.5px solid #ddd",
  background: "#fff",
  // Explicit dark text: these controls sit inside the green header, whose
  // `color: #fff` browsers can inherit into <input>/<select> text, making the
  // value invisible (white text on the white field background) even though a
  // real value is selected underneath.
  color: "#2c2c2c",
};

const labelStyle = {
  fontSize: "12px",
  color: "#e8f0d8",
  fontWeight: "600",
  marginBottom: "6px",
};

const tileStyle = {
  background: "#fff",
  borderRadius: "14px",
  padding: "18px 20px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  border: "1px solid #e8f0d8",
};

function pct(part, whole) {
  if (!whole) return "0.0";
  return ((part / whole) * 100).toFixed(1);
}

const VisitorFunnel = () => {
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [hubs, setHubs] = useState([]);
  const [hubId, setHubId] = useState("");
  const [session, setSession] = useState("");
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cumulative, setCumulative] = useState(true);
  const [snapshot, setSnapshot] = useState(null); // clicked timeline point

  useEffect(() => {
    axios
      .get(`${API}/Hub/hubs`)
      .then((res) => {
        const list = (Array.isArray(res.data) ? res.data : [])
          .slice()
          .sort((a, b) => (a.hubName || "").localeCompare(b.hubName || ""));
        setHubs(list);
        if (list.length > 0) setHubId(list[0]._id);
      })
      .catch((err) => console.error("Failed to load hubs", err));
  }, []);

  const fetchFunnel = useCallback(async () => {
    if (!hubId) return;
    try {
      setLoading(true);
      setSnapshot(null);
      const res = await axios.get(`${API}/admin/hub-visits/funnel`, {
        params: { date, hubId, session: session || undefined },
      });
      if (res.data.success) {
        setStats(res.data.stats);
        setTimeline(res.data.timeline);
      }
    } catch (err) {
      console.error(err);
      setStats(null);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, [date, hubId, session]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);

  const handleExport = () => {
    if (!hubId) return;
    const params = new URLSearchParams({ date, hubId });
    if (session) params.append("session", session);
    window.open(
      `${API}/admin/hub-visits/funnel/export?${params.toString()}`,
      "_blank",
    );
  };

  const chartData = {
    labels: timeline.map((t) => t.time),
    datasets: [
      {
        label: cumulative ? "Site view (cumulative)" : "Site view",
        data: timeline.map((t) =>
          cumulative ? t.siteViewCumulative : t.siteView,
        ),
        borderColor: "#c8a266",
        backgroundColor: "#c8a266",
        borderDash: [4, 3],
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: cumulative
          ? "Registered visitors (cumulative)"
          : "Registered visitors",
        data: timeline.map((t) =>
          cumulative ? t.registeredVisitorsCumulative : t.registeredVisitors,
        ),
        borderColor: "#8b4513",
        backgroundColor: "#8b4513",
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: cumulative ? "Orders (cumulative)" : "Orders",
        data: timeline.map((t) => (cumulative ? t.ordersCumulative : t.orders)),
        borderColor: "#3a5a0e",
        backgroundColor: "#3a5a0e",
        borderWidth: 2.5,
        pointRadius: 2,
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        align: "start",
        labels: { boxWidth: 14, usePointStyle: false },
      },
      tooltip: { enabled: true },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Count" } },
    },
    onClick: (evt, elements) => {
      if (!elements || elements.length === 0) return;
      const idx = elements[0].index;
      const point = timeline[idx];
      if (!point) return;
      const base = point.siteViewCumulative || 0;
      setSnapshot({
        time: point.time,
        siteView: point.siteViewCumulative,
        registered: point.registeredVisitorsCumulative,
        orders: point.ordersCumulative,
        siteViewPct: 100,
        registeredPct: pct(point.registeredVisitorsCumulative, base),
        ordersPct: pct(point.ordersCumulative, base),
      });
    },
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Inter, sans-serif",
        background: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #6B8E23 0%, #4a6e12 100%)",
          borderRadius: "16px",
          padding: "24px 28px",
          color: "#fff",
          marginBottom: "24px",
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "700" }}>
            DailyDish — Visitors, orders &amp; drop-off
          </h2>
          <p style={{ margin: "4px 0 0", opacity: 0.85, fontSize: "14px" }}>
            Click any point on the timeline to see that exact moment
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <div>
            <div style={labelStyle}>Date</div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={selectStyle}
            />
          </div>
          <div>
            <div style={labelStyle}>Drop point</div>
            <select
              value={hubId}
              onChange={(e) => setHubId(e.target.value)}
              style={selectStyle}
            >
              {hubs.map((h) => (
                <option key={h._id} value={h._id}>
                  {h.hubName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={labelStyle}>Session</div>
            <select
              value={session}
              onChange={(e) => setSession(e.target.value)}
              style={selectStyle}
            >
              <option value="">All Sessions</option>
              {SESSIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={!hubId}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: "none",
              background: "#e0a326",
              color: "#fff",
              fontWeight: "700",
              cursor: hubId ? "pointer" : "not-allowed",
            }}
          >
            Export visitors (CSV)
          </button>
        </div>
      </div>

      {loading || !stats ? (
        <div style={{ textAlign: "center", padding: "60px", color: "#888" }}>
          {loading ? "Loading..." : "No data for this selection."}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={tileStyle}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                Site View (pre + post login)
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#3a5a0e",
                }}
              >
                {stats.siteView}
              </div>
            </div>
            <div style={tileStyle}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                Registered Visitors
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#3a5a0e",
                }}
              >
                {stats.registeredVisitors}
              </div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                {stats.orderedPct}% ordered
              </div>
            </div>
            <div style={tileStyle}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                Total Orders
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#3a5a0e",
                }}
              >
                {stats.totalOrders}
              </div>
            </div>
            <div style={tileStyle}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                Visitors Who Ordered
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#3a5a0e",
                }}
              >
                {stats.totalOrdered}
              </div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                of tracked visitors that day
              </div>
            </div>
            <div style={tileStyle}>
              <div
                style={{
                  fontSize: "11px",
                  color: "#888",
                  fontWeight: "700",
                  textTransform: "uppercase",
                }}
              >
                Left Without Ordering
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#b3401f",
                }}
              >
                {stats.leftWithoutOrdering}
              </div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                {stats.leftPct}% of registered visitors
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "20px 24px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              border: "1px solid #e8f0d8",
            }}
          >
            <h3
              style={{
                margin: "0 0 4px",
                fontSize: "16px",
                color: "#3a5a0e",
                fontWeight: "700",
              }}
            >
              Visitors vs. orders — {moment(date).format("D MMM YYYY")}
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#888" }}>
              Click any point on the line to see the snapshot at that moment.
            </p>

            <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
              <button
                onClick={() => setCumulative(true)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "20px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer",
                  background: cumulative ? "#6B8E23" : "#eee",
                  color: cumulative ? "#fff" : "#555",
                }}
              >
                Cumulative (running total)
              </button>
              <button
                onClick={() => setCumulative(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "20px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "13px",
                  cursor: "pointer",
                  background: !cumulative ? "#6B8E23" : "#eee",
                  color: !cumulative ? "#fff" : "#555",
                }}
              >
                Last 5 min only
              </button>
            </div>

            {timeline.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#888" }}
              >
                No visits recorded for this date.
              </div>
            ) : (
              <div style={{ position: "relative", height: "360px" }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            )}

            {snapshot && (
              <div
                style={{
                  marginTop: "16px",
                  background: "#2c2c2c",
                  color: "#fff",
                  borderRadius: "10px",
                  padding: "14px 18px",
                  maxWidth: "260px",
                  fontSize: "13px",
                }}
              >
                <div style={{ fontWeight: "700", marginBottom: "6px" }}>
                  {snapshot.time}
                </div>
                <div>
                  Site view: {snapshot.siteView} ({snapshot.siteViewPct}%)
                </div>
                <div>
                  Registered: {snapshot.registered} ({snapshot.registeredPct}%)
                </div>
                <div>
                  Ordered: {snapshot.orders} ({snapshot.ordersPct}%)
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VisitorFunnel;
